import axios from 'axios';
import partition from 'lodash/partition';
import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  InviteCommand,
  OrderInviteCarriersToSpotMarketBatchCommand,
  ShipmentInviteCarriersToSpotMarketBatchCommand,
  ShipmentInviteCarriersToSpotMarketCommand,
  ShipmentSpecInviteCarriersToSpotMarketCommand,
  SpotMarket,
} from '@/model';
import { isCompletedStatus } from '@/model/carrierPortal/SpotMarketStatus';

// Identifies which entity owns the rate shop being loaded. The `?: never` on
// the unused branch lets callers narrow via direct property access (e.g.
// `opts.shipmentId !== undefined`) and prevents both ids from being set.
export type RateShopOwner =
  | { shipmentId: number; shipmentSpecId?: never }
  | { shipmentSpecId: number; shipmentId?: never };

export type PerformLoadOptions = RateShopOwner & {
  bypassCache: boolean;
};

export interface InviteCarriersResult {
  validSpotMarkets: SpotMarket[];
  erroredSpotMarkets: SpotMarket[];
}

export interface SpotMarketStore {
  inviteCarriersToSpotMarket: (
    command: InviteCommand,
  ) => Promise<InviteCarriersResult>;
  loadSpotMarkets: (
    rateShopId: number,
    opts: PerformLoadOptions,
  ) => Promise<void>;
  refreshSpotMarkets: (
    rateShopId: number,
    opts: RateShopOwner,
  ) => Promise<void>;
  /** A map of rate shop ids to the related spot market */
  spotMarkets: Ref<Record<number, Loadable<SpotMarket[]>>>;
}

const _useSpotMarketStore = defineStore('spotMarket', (): SpotMarketStore => {
  const { t } = useI18n();
  const toast = useToast();

  const spotMarkets: Ref<Record<number, Loadable<SpotMarket[]>>> = ref({});

  const loadSpotMarkets = async (
    rateShopId: number,
    opts: PerformLoadOptions,
  ) => {
    const spotMarketsForRateShop = spotMarkets.value[rateShopId];
    const numSpotMarkets = spotMarketsForRateShop?.data?.length ?? 0;
    const isLoading = spotMarketsForRateShop?.loading ?? false;
    const requiresLoad = numSpotMarkets === 0 && !isLoading;

    if (opts.bypassCache || requiresLoad) {
      return performLoad(rateShopId, { ...opts, isPolling: false });
    }
  };

  const refreshSpotMarkets = async (
    rateShopId: number,
    opts: RateShopOwner,
  ) => {
    return performLoad(rateShopId, { ...opts, isPolling: true });
  };

  const performLoad = async (
    rateShopId: number,
    opts: RateShopOwner & { isPolling: boolean },
  ) => {
    if (!spotMarkets.value[rateShopId]) {
      spotMarkets.value[rateShopId] = { loading: !opts.isPolling };
    }
    if (!opts.isPolling) {
      spotMarkets.value[rateShopId].loading = true;
    }

    try {
      const incomingSpotMarkets =
        opts.shipmentId !== undefined
          ? await api.shipment.findSpotMarketsByRateShopId(
              opts.shipmentId,
              rateShopId,
            )
          : await api.shipmentSpec.findSpotMarketsByRateShopId(
              opts.shipmentSpecId,
              rateShopId,
            );

      const existingSpotMarkets = spotMarkets.value[rateShopId].data ?? [];
      const isSpotMarketPollingCompleted =
        existingSpotMarkets.length > 0 &&
        existingSpotMarkets.every((sm) => isCompletedStatus(sm.status));

      if (opts.isPolling && isSpotMarketPollingCompleted) {
        return;
      }

      spotMarkets.value[rateShopId].data = incomingSpotMarkets;
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      spotMarkets.value[rateShopId].error = e;
      console.error('Error loading spot markets', e);
      if (!opts.isPolling) {
        toast.addError({
          detail: t('errors.loadingParam', {
            param: t('carrierPortal.spotMarket.spotMarkets', 2),
          }),
          error: e,
        });
      }
    } finally {
      if (!opts.isPolling) {
        spotMarkets.value[rateShopId].loading = false;
      }
    }
  };

  const handleNewSpotMarkets = (newSpotMarkets: SpotMarket[]) => {
    newSpotMarkets.forEach((s) => {
      const rateShopId = s.rateShopId;
      if (!rateShopId) {
        return;
      }

      if (!spotMarkets.value[rateShopId]) {
        spotMarkets.value[rateShopId] = { loading: false };
      }

      if (spotMarkets.value[rateShopId].data !== undefined) {
        const index = spotMarkets.value[rateShopId].data.findIndex(
          (existingSpotMarket) => existingSpotMarket.id === s.id,
        );

        if (index === -1) {
          spotMarkets.value[rateShopId].data.push(s);
        } else {
          spotMarkets.value[rateShopId].data.splice(index, 1, s);
        }
      } else {
        spotMarkets.value[rateShopId].data = [s];
      }
    });
  };

  const inviteCarriersToSpotMarket = async (
    command: InviteCommand,
  ): Promise<InviteCarriersResult> => {
    try {
      command.validate();

      let newSpotMarkets: SpotMarket[] = [];
      if (command instanceof ShipmentSpecInviteCarriersToSpotMarketCommand) {
        const typedCommand =
          command as ShipmentSpecInviteCarriersToSpotMarketCommand;
        const shipmentSpecId = typedCommand.shipmentSpecId!;
        newSpotMarkets = await api.shipmentSpec.inviteCarriersToSpotMarket(
          shipmentSpecId,
          command,
        );
      } else if (command instanceof ShipmentInviteCarriersToSpotMarketCommand) {
        const typedCommand =
          command as ShipmentInviteCarriersToSpotMarketCommand;
        const shipmentId = typedCommand.shipmentId!;
        newSpotMarkets = await api.shipment.inviteCarriersToSpotMarket(
          shipmentId,
          command,
        );
      } else if (
        command instanceof OrderInviteCarriersToSpotMarketBatchCommand
      ) {
        newSpotMarkets =
          await api.orderBatch.inviteCarriersToSpotMarket(command);
      } else if (
        command instanceof ShipmentInviteCarriersToSpotMarketBatchCommand
      ) {
        newSpotMarkets =
          await api.shipmentBatch.inviteCarriersToSpotMarket(command);
      }

      const [validSpotMarkets, erroredSpotMarkets] = partition(
        newSpotMarkets,
        (s) => !s.hasErrors,
      );
      handleNewSpotMarkets(validSpotMarkets);
      toast.addSuccess({
        detail: t('messages.successSendingParam', {
          param: t('carrierPortal.spotMarket.invites', command.scacs.length),
        }),
      });

      return { validSpotMarkets, erroredSpotMarkets };
    } catch (e) {
      console.error('Error inviting carriers to spot market', e);
      toast.addError({
        detail: t('errors.sendingParam', {
          param: t('carrierPortal.spotMarket.invites', command.scacs.length),
        }),
        error: e,
      });

      // Rethrow `e` so callers can perform any specific handling
      throw e;
    }
  };

  return {
    inviteCarriersToSpotMarket,
    loadSpotMarkets,
    refreshSpotMarkets,
    spotMarkets,
  };
});

export const useSpotMarketStore = (): SpotMarketStore => {
  const store = _useSpotMarketStore();
  const { spotMarkets } = storeToRefs(store);
  const { inviteCarriersToSpotMarket, loadSpotMarkets, refreshSpotMarkets } =
    store;

  return {
    inviteCarriersToSpotMarket,
    loadSpotMarkets,
    refreshSpotMarkets,
    spotMarkets,
  };
};
